import { createHash } from 'node:crypto';
import { chmodSync, createReadStream, existsSync } from 'node:fs';
import { resolvePaths } from '../../client/config.js';
import { CredentialStore } from '../../crypto/storage.js';
import { OnlineClient, type OnlineQueryValue } from '../../online/client.js';
import { ONLINE_API_ORIGIN, onlineOperationPath } from '../../online/contract.js';
import { resolveOnlineCredentials } from '../../online/credentials.js';
import { OnlineApiError } from '../../online/errors.js';
import { fetchOnlinePages } from '../../online/pagination.js';
import { packageVersion } from '../../version.js';
import type { ParsedArgs } from '../args.js';
import { flag, hasFlag, numberFlag } from '../args.js';
import { CliError, ExitCode } from '../errors.js';
import { writeBytesOutput } from '../files.js';
import type { CommandResult } from '../output.js';
import { readSecret } from '../secret.js';

export interface OnlineCommandDependencies {
  fetch?: typeof fetch;
  readSecret?: typeof readSecret;
  sleep?: (milliseconds: number) => Promise<void>;
  now?: () => number;
  random?: () => number;
}

interface OnlineContext {
  client: OnlineClient;
  source: 'environment' | 'saved' | 'none';
  environmentConfigured: boolean;
  savedConfigured: boolean;
}

export async function onlineCommand(
  args: ParsedArgs,
  dependencies: OnlineCommandDependencies = {},
): Promise<CommandResult> {
  const area = args.positionals[1] ?? 'status';
  switch (area) {
    case 'auth':
      return authCommand(args, dependencies);
    case 'status':
      return onlineStatus(args, dependencies);
    case 'profile':
      return jsonRequest(args, dependencies, onlineOperationPath('profile'));
    case 'sessions':
      return sessionsCommand(args, dependencies);
    case 'cards':
      return cardsCommand(args, dependencies);
    case 'decks':
      return decksCommand(args, dependencies);
    case 'devices':
      return devicesCommand(args, dependencies);
    case 'backups':
      return backupsCommand(args, dependencies);
    case 'request':
      return rawRequest(args, dependencies);
    default:
      throw new CliError(`Unknown online command "${area}"`, ExitCode.Usage);
  }
}

async function authCommand(
  args: ParsedArgs,
  dependencies: OnlineCommandDependencies,
): Promise<CommandResult> {
  const action = args.positionals[2] ?? 'status';
  const { credentialsPath } = resolvePaths(args.options.configPath, args.options.credentialsPath);
  const store = new CredentialStore(credentialsPath);

  if (action === 'set') {
    const apiKey = await (dependencies.readSecret ?? readSecret)();
    if (!apiKey) throw new CliError('Online User API key is required', ExitCode.Usage);
    store.saveOnlineApiKey(apiKey);
    const environmentConfigured = Boolean(process.env.ZAPAROO_ONLINE_USER_API_KEY?.trim());
    const message = environmentConfigured
      ? 'Saved Online User API key; environment override remains active'
      : 'Saved Online User API key';
    return {
      data: {
        success: true,
        saved: true,
        activeSource: environmentConfigured ? 'environment' : 'saved',
      },
      human: message,
    };
  }

  if (action === 'forget') {
    const deleted = store.deleteOnlineApiKey();
    const status = resolveOnlineCredentials(store);
    return {
      data: {
        success: true,
        deleted,
        activeSource: status.source,
        environmentConfigured: status.environmentConfigured,
      },
      human: deleted ? 'Forgot saved Online User API key' : 'No saved Online User API key',
    };
  }

  if (action === 'status') {
    const status = resolveOnlineCredentials(store);
    return {
      data: {
        configured: status.source !== 'none',
        source: status.source,
        environmentConfigured: status.environmentConfigured,
        savedConfigured: status.savedConfigured,
      },
      human: `Online User API credentials: ${status.source}`,
    };
  }

  throw new CliError(`Unknown online auth action "${action}"`, ExitCode.Usage);
}

async function onlineStatus(
  args: ParsedArgs,
  dependencies: OnlineCommandDependencies,
): Promise<CommandResult> {
  const context = createContext(args, dependencies);
  const response = await context.client.get(onlineOperationPath('status'), {
    authenticate: false,
  });
  return {
    data: {
      api: response.data,
      credentials: {
        configured: context.source !== 'none',
        source: context.source,
        environmentConfigured: context.environmentConfigured,
        savedConfigured: context.savedConfigured,
      },
    },
    human: `Online User API available; credentials: ${context.source}`,
  };
}

async function sessionsCommand(
  args: ParsedArgs,
  dependencies: OnlineCommandDependencies,
): Promise<CommandResult> {
  const action = args.positionals[2] ?? 'list';
  if (action === 'active' && hasFlag(args.flags, 'watch')) {
    return watchActiveSessions(args, dependencies);
  }
  if (action === 'active') {
    return jsonRequest(args, dependencies, onlineOperationPath('sessions.active'));
  }
  if (action === 'list') {
    return paginatedRequest(args, dependencies, onlineOperationPath('sessions.list'), {
      device: flag(args.flags, 'device'),
      profile: flag(args.flags, 'profile'),
      system: flag(args.flags, 'system'),
      since: flag(args.flags, 'since'),
      until: flag(args.flags, 'until'),
      limit: limitFlag(args),
      cursor: flag(args.flags, 'cursor'),
    });
  }
  if (action === 'summary') {
    return paginatedRequest(args, dependencies, onlineOperationPath('sessions.summary'), {
      group: flag(args.flags, 'group'),
      since: flag(args.flags, 'since'),
      until: flag(args.flags, 'until'),
      limit: limitFlag(args),
      cursor: flag(args.flags, 'cursor'),
    });
  }
  throw new CliError(`Unknown online sessions action "${action}"`, ExitCode.Usage);
}

async function cardsCommand(
  args: ParsedArgs,
  dependencies: OnlineCommandDependencies,
): Promise<CommandResult> {
  const action = args.positionals[2] ?? 'list';
  if (action !== 'list')
    throw new CliError(`Unknown online cards action "${action}"`, ExitCode.Usage);
  return paginatedRequest(args, dependencies, onlineOperationPath('cards.list'), {
    name: flag(args.flags, 'name'),
    limit: limitFlag(args),
    cursor: flag(args.flags, 'cursor'),
  });
}

async function decksCommand(
  args: ParsedArgs,
  dependencies: OnlineCommandDependencies,
): Promise<CommandResult> {
  const action = args.positionals[2] ?? 'list';
  if (action === 'list') {
    return paginatedRequest(args, dependencies, onlineOperationPath('decks.list'), {
      name: flag(args.flags, 'name'),
      limit: limitFlag(args),
      cursor: flag(args.flags, 'cursor'),
    });
  }
  const shortId = requiredPositional(args, 3, `online decks ${action} requires <short-id>`);
  if (action === 'get') {
    return jsonRequest(
      args,
      dependencies,
      onlineOperationPath('decks.get', { short_id: shortId }),
      {
        limit: limitFlag(args),
      },
    );
  }
  if (action === 'cards') {
    return paginatedRequest(
      args,
      dependencies,
      onlineOperationPath('decks.cards', { short_id: shortId }),
      {
        limit: limitFlag(args),
        cursor: flag(args.flags, 'cursor'),
      },
    );
  }
  throw new CliError(`Unknown online decks action "${action}"`, ExitCode.Usage);
}

async function devicesCommand(
  args: ParsedArgs,
  dependencies: OnlineCommandDependencies,
): Promise<CommandResult> {
  const action = args.positionals[2] ?? 'list';
  if (action !== 'list') {
    throw new CliError(`Unknown online devices action "${action}"`, ExitCode.Usage);
  }
  return paginatedRequest(args, dependencies, onlineOperationPath('devices.list'), {
    limit: limitFlag(args),
    cursor: flag(args.flags, 'cursor'),
  });
}

async function backupsCommand(
  args: ParsedArgs,
  dependencies: OnlineCommandDependencies,
): Promise<CommandResult> {
  const action = args.positionals[2] ?? 'list';
  const deviceId = requiredPositional(args, 3, `online backups ${action} requires <device-id>`);
  if (action === 'list') {
    return paginatedRequest(
      args,
      dependencies,
      onlineOperationPath('backups.list', { device_id: deviceId }),
      {
        limit: limitFlag(args),
        cursor: flag(args.flags, 'cursor'),
      },
    );
  }
  const backupId = requiredPositional(args, 4, `online backups ${action} requires <backup-id>`);
  if (action === 'files') {
    return paginatedRequest(
      args,
      dependencies,
      onlineOperationPath('backups.files', { device_id: deviceId, backup_id: backupId }),
      {
        category: flag(args.flags, 'category'),
        limit: limitFlag(args),
        cursor: flag(args.flags, 'cursor'),
      },
    );
  }
  if (action === 'download') {
    const sha256 = requiredPositional(
      args,
      5,
      'online backups download requires <sha256> --output <path>',
    ).toLowerCase();
    const output = flag(args.flags, 'output');
    if (!output) {
      throw new CliError('online backups download requires --output <path>', ExitCode.Usage);
    }
    if (!/^[0-9a-f]{64}$/.test(sha256)) {
      throw new CliError(
        'backup SHA-256 must contain 64 lowercase hexadecimal characters',
        ExitCode.Usage,
      );
    }
    return downloadBackup(args, dependencies, deviceId, backupId, sha256, output);
  }
  throw new CliError(`Unknown online backups action "${action}"`, ExitCode.Usage);
}

async function rawRequest(
  args: ParsedArgs,
  dependencies: OnlineCommandDependencies,
): Promise<CommandResult> {
  const path = requiredPositional(args, 2, 'online request requires a /v1 path');
  let url: URL;
  try {
    url = new URL(path, ONLINE_API_ORIGIN);
  } catch {
    throw new CliError('online request requires a valid official /v1 path', ExitCode.Usage);
  }
  if (
    !path.startsWith('/') ||
    url.origin !== ONLINE_API_ORIGIN ||
    (url.pathname !== '/v1' && !url.pathname.startsWith('/v1/'))
  ) {
    throw new CliError('online request requires a valid official /v1 path', ExitCode.Usage);
  }
  const context = createContext(args, dependencies);
  const response = await context.client.get(path, {
    authenticate: url.pathname !== onlineOperationPath('status'),
  });
  return { data: response.data };
}

async function jsonRequest(
  args: ParsedArgs,
  dependencies: OnlineCommandDependencies,
  path: string,
  query: Record<string, OnlineQueryValue> = {},
): Promise<CommandResult> {
  const context = createContext(args, dependencies);
  const response = await context.client.get(path, { query });
  return { data: response.data };
}

async function paginatedRequest(
  args: ParsedArgs,
  dependencies: OnlineCommandDependencies,
  path: string,
  query: Record<string, OnlineQueryValue>,
): Promise<CommandResult> {
  const context = createContext(args, dependencies);
  const data = await fetchOnlinePages(
    context.client,
    path,
    query,
    hasFlag(args.flags, 'all-pages'),
    maxPagesFlag(args),
  );
  return { data };
}

async function downloadBackup(
  args: ParsedArgs,
  dependencies: OnlineCommandDependencies,
  deviceId: string,
  backupId: string,
  expectedSha256: string,
  outputPath: string,
): Promise<CommandResult> {
  const context = createContext(args, dependencies);
  const currentHash = existsSync(outputPath) ? await hashFile(outputPath) : undefined;
  const response = await context.client.get(
    onlineOperationPath('backups.download', {
      device_id: deviceId,
      backup_id: backupId,
      sha256: expectedSha256,
    }),
    {
      responseType: 'binary',
      headers: currentHash === expectedSha256 ? { 'If-None-Match': expectedSha256 } : undefined,
    },
  );
  if (response.status === 304) {
    chmodSync(outputPath, 0o600);
    return {
      data: { output: outputPath, sha256: expectedSha256, unchanged: true },
      human: `Backup file is unchanged: ${outputPath}`,
    };
  }
  if (!response.bytes)
    throw new OnlineApiError('Backup response contains no file data', 'invalid-response');
  const downloadedHash = hashBytes(response.bytes);
  const headerHash = response.sha256?.toLowerCase();
  const etagHash = response.etag?.replace(/^W\//, '').replace(/^"|"$/g, '').toLowerCase();
  if (!headerHash || !etagHash) {
    throw new OnlineApiError(
      'Backup response is missing required hash headers',
      'invalid-response',
    );
  }
  if (
    downloadedHash !== expectedSha256 ||
    headerHash !== expectedSha256 ||
    etagHash !== expectedSha256
  ) {
    throw new OnlineApiError(
      'Downloaded backup SHA-256 does not match requested object',
      'hash-mismatch',
    );
  }
  writeBytesOutput(response.bytes, outputPath);
  return {
    data: {
      output: outputPath,
      size: response.bytes.length,
      sha256: downloadedHash,
      unchanged: false,
    },
    human: `Downloaded backup file to ${outputPath}`,
  };
}

async function watchActiveSessions(
  args: ParsedArgs,
  dependencies: OnlineCommandDependencies,
): Promise<CommandResult> {
  if (!args.options.jsonl) {
    throw new CliError('online sessions active --watch requires --jsonl', ExitCode.Usage);
  }
  const seconds = numberFlag(args.flags, 'seconds') ?? 30;
  if (!Number.isFinite(seconds) || seconds <= 0) {
    throw new CliError('--seconds must be a positive number', ExitCode.Usage);
  }
  const context = createContext(args, dependencies);
  const sleep =
    dependencies.sleep ??
    ((milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds)));
  const now = dependencies.now ?? Date.now;
  const random = dependencies.random ?? Math.random;
  const deadline = now() + seconds * 1000;
  let etag: string | undefined;
  let emitted = 0;

  while (now() < deadline) {
    let response: Awaited<ReturnType<OnlineClient['get']>>;
    try {
      response = await context.client.get(onlineOperationPath('sessions.active'), {
        headers: etag ? { 'If-None-Match': etag } : undefined,
      });
    } catch (error) {
      const retryAfter =
        error instanceof OnlineApiError && error.kind === 'rate-limit'
          ? error.details.retryAfter
          : undefined;
      if (retryAfter === undefined) throw error;
      const remaining = deadline - now();
      if (remaining <= 0) break;
      await sleep(Math.min(retryAfter * 1000, remaining));
      continue;
    }
    if (response.etag) etag = response.etag;
    if (response.status !== 304) {
      process.stdout.write(`${JSON.stringify(response.data)}\n`);
      emitted++;
    }
    const remaining = deadline - now();
    if (remaining <= 0) break;
    const interval = Math.max(10, response.pollInterval ?? 10) * 1000;
    const jitter = Math.floor(random() * 500);
    await sleep(Math.min(interval + jitter, remaining));
  }

  return { data: { emitted, seconds }, streamed: true };
}

function createContext(args: ParsedArgs, dependencies: OnlineCommandDependencies): OnlineContext {
  const { credentialsPath } = resolvePaths(args.options.configPath, args.options.credentialsPath);
  const store = new CredentialStore(credentialsPath);
  const credentials = resolveOnlineCredentials(store);
  return {
    client: new OnlineClient({
      apiKey: credentials.apiKey,
      timeoutMs: args.options.timeoutSeconds * 1000,
      userAgent: `@zaparoo/cli/${packageVersion}`,
      fetch: dependencies.fetch,
    }),
    source: credentials.source,
    environmentConfigured: credentials.environmentConfigured,
    savedConfigured: credentials.savedConfigured,
  };
}

function requiredPositional(args: ParsedArgs, index: number, message: string): string {
  const value = args.positionals[index];
  if (!value) throw new CliError(message, ExitCode.Usage);
  return value;
}

function limitFlag(args: ParsedArgs): number | undefined {
  const value = numberFlag(args.flags, 'limit');
  if (value === undefined) return undefined;
  if (!Number.isInteger(value) || value < 1 || value > 500) {
    throw new CliError('--limit must be an integer between 1 and 500', ExitCode.Usage);
  }
  return value;
}

function maxPagesFlag(args: ParsedArgs): number | undefined {
  const value = numberFlag(args.flags, 'max-pages');
  if (value === undefined) return undefined;
  if (!Number.isInteger(value) || value < 1 || value > 100) {
    throw new CliError('--max-pages must be an integer between 1 and 100', ExitCode.Usage);
  }
  return value;
}

async function hashFile(path: string): Promise<string> {
  const hash = createHash('sha256');
  for await (const chunk of createReadStream(path)) hash.update(chunk);
  return hash.digest('hex');
}

function hashBytes(bytes: Uint8Array): string {
  return createHash('sha256').update(bytes).digest('hex');
}
